/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import { type GenerateReportPayload, type ReportJob, reportsApi } from '../api/reports.js';

const REPORTS_KEY = ['reports'] as const;

/** Are any jobs still being worked on (so the list should keep polling)? */
function hasPending(jobs: readonly ReportJob[] | undefined): boolean {
  return (jobs ?? []).some((job) => job.status === 'queued' || job.status === 'running');
}

export function useReports(): UseQueryResult<readonly ReportJob[]> {
  return useQuery({
    queryKey: REPORTS_KEY,
    queryFn: () => reportsApi.list(),
    // Poll every 2s while any job is still queued or running, otherwise stop.
    refetchInterval: (query) => (hasPending(query.state.data) ? 2000 : false),
  });
}

export function useGenerateReport(): UseMutationResult<ReportJob, Error, GenerateReportPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GenerateReportPayload) => reportsApi.generate(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REPORTS_KEY });
    },
  });
}

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_TRIES = 40;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Poll a job to a terminal state, then resolve with the finished row. */
async function waitForJob(id: string): Promise<ReportJob> {
  for (let attempt = 0; attempt < POLL_MAX_TRIES; attempt += 1) {
    const job = await reportsApi.get(id);
    if (job.status === 'succeeded' || job.status === 'failed') {
      return job;
    }
    await delay(POLL_INTERVAL_MS);
  }
  throw new Error('Report timed out');
}

/**
 * One-shot "Export PDF" for a therapy plan (BRD M01 #8): enqueue an `itp`
 * report, poll it to completion, then stream the artifact to the browser.
 * Surfaced as a mutation so callers get `isPending`/`isError` for free.
 */
export function useExportPlanPdf(): UseMutationResult<void, Error, string> {
  return useMutation({
    mutationFn: async (planId: string) => {
      const job = await reportsApi.generate({ reportType: 'itp', planId });
      const finished = await waitForJob(job.id);
      if (finished.status === 'failed') {
        throw new Error(finished.error ?? 'Report failed');
      }
      await downloadReport(finished.id, finished.fileName ?? 'therapy-plan.pdf');
    },
  });
}

/**
 * Fetch the PDF blob and hand it to the browser as a download. Kept out of the
 * component so the object-URL lifecycle (create → click → revoke) lives in one
 * place.
 */
export async function downloadReport(id: string, fileName: string): Promise<void> {
  const blob = await reportsApi.download(id);
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
